// #34 Avatar, Avatars and Person (DC-6: initials only, never a photograph). A person is a circle with their initials in
// a hue ring (--mc-hue); a candidate is a rounded-square tile on a tint (--mc-hue-bg). Hues come from the id (personHue,
// candidateHue) unless given. Sizes 24 · 32 · 36 · 44 · 72. An avatar is focusable only when it is a link (href).
// Person is the avatar, the name, a sub line (title or seat) and a right slot; with href the name is the link.

import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { candidateHue, personHue } from "../lib/personHue";
import { vars, type McVarValue } from "../lib/vars";

export type AvatarSize = 18 | 24 | 32 | 36 | 44 | 72;

/** Two initials: the first letters of the first and the last word of the name, counting only words that start with a
 * letter, so a bracketed note such as "Talent Acquisition (system)" draws "TA", never "T(". */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => /^\p{L}/u.test(word));
  if (words.length === 0) return "";
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? words[words.length - 1]?.[0] ?? "" : "";
  return `${first}${last}`.toUpperCase();
}

export interface AvatarProps {
  /** The person's or candidate's name: the initials, and the accessible name. */
  name: string;
  /** The stable key the hue is taken from (an id); default the name. */
  hueKey?: string | undefined;
  /** A person's ring hue (a --p-* token var); default personHue(hueKey). */
  hue?: McVarValue | undefined;
  /** A candidate: the rounded tile on a tint. */
  candidate?: boolean | undefined;
  /** A candidate's tint (a --t-* token var); default candidateHue(hueKey). */
  tint?: McVarValue | undefined;
  size?: AvatarSize | undefined;
  /** The signed-in person's own avatar: a blue outline. */
  you?: boolean | undefined;
  /** A link to the person's or candidate's screen: the only focusable form. */
  href?: string | undefined;
  /** Beside the name already (inside Person): hidden from assistive technology. */
  decorative?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function Avatar({ name, hueKey, hue, candidate, tint, size = 32, you, href, decorative, hs, className }: AvatarProps) {
  const hot = useHs(hs);
  const key = hueKey ?? name;
  const style = candidate ? vars({ "--mc-hue-bg": tint ?? candidateHue(key) }) : vars({ "--mc-hue": hue ?? personHue(key) });
  const classes = cx("mc-av", size !== 32 && `mc-s${size}`, candidate && "mc-cand", you && "mc-av-you", href !== undefined && "mc-av-link", className);
  const initials = initialsOf(name);
  if (href !== undefined) {
    return <a href={href} className={classes} style={style} aria-label={name} {...hot}><span aria-hidden="true">{initials}</span></a>;
  }
  if (decorative) return <span className={classes} style={style} aria-hidden="true">{initials}</span>;
  return <span className={classes} style={style} role="img" aria-label={name}><span aria-hidden="true">{initials}</span></span>;
}

export interface AvatarsProps {
  /** People (or candidates) in order; each is named for assistive technology. */
  items: readonly Omit<AvatarProps, "size" | "decorative" | "href" | "hs">[];
  /** 24 (default) or 32. */
  size?: 24 | 32 | undefined;
  /** At most this many are drawn; the rest are a +n count. */
  max?: number | undefined;
  className?: string | undefined;
}

export function Avatars({ items, size = 24, max, className }: AvatarsProps) {
  const drawn = max !== undefined && items.length > max ? items.slice(0, max) : items;
  const more = items.length - drawn.length;
  return (
    <span className={cx("mc-avs", className)}>
      {drawn.map((item, index) => <Avatar key={`${item.hueKey ?? item.name}-${index}`} {...item} size={size} />)}
      {more > 0 ? <span className={cx("mc-av", "mc-av-more", size === 24 && "mc-s24")}>{`+${more}`}</span> : null}
    </span>
  );
}

export interface PersonProps extends Omit<AvatarProps, "decorative" | "you" | "className"> {
  /** The second line: title, seat or a meta line; absent, no second line. */
  sub?: ReactNode | undefined;
  /** The signed-in person: the avatar's outline, and these words after the name when given. */
  you?: boolean | string | undefined;
  /** Right-aligned content (a pill, an action). */
  right?: ReactNode | undefined;
  className?: string | undefined;
}

export function Person({ name, sub, you, right, href, hs, size = 32, className, ...avatar }: PersonProps) {
  const hot = useHs(hs);
  return (
    <div className={cx("mc-person", className)}>
      <Avatar {...avatar} name={name} size={size} you={Boolean(you)} decorative />
      <div className="mc-pn">
        <b>
          {href !== undefined ? <a className="mc-person-link" href={href} {...hot}>{name}</a> : name}
          {typeof you === "string" ? <span className="mc-you">{you}</span> : null}
        </b>
        {sub !== undefined && sub !== null ? <span>{sub}</span> : null}
      </div>
      {right ? <div className="mc-person-r">{right}</div> : null}
    </div>
  );
}
